import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import "moment-timezone";
import Skeleton from "@mui/material/Skeleton";
import { GlobalStyles, Grid, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import StorefrontIcon from "@mui/icons-material/Storefront";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SalesDateRangePicker, {
  getIsoWeekRange,
  getMtdRevenueCompareRanges,
  getPeriodDateRange,
  getYtdDateRange,
  PERIOD_MTD,
  PERIOD_YTD,
} from "./components/SalesDateRangePicker";
import ClientDirectoryWidget, {
  normalizeFleetAndOverdue,
} from "./components/ClientDirectoryWidget";
import FleetMakeModelWidget from "./components/FleetMakeModelWidget";
import ProductMixPmsWidget, {
  normalizeProductMix,
} from "./components/ProductMixPmsWidget";
import {
  Tip,
  WidgetInfo,
  WidgetSkeleton,
  formatCurrency,
  fullCurrency,
  fullNumber,
  fullPct,
  num,
  pct,
  pick,
} from "./components/skdShared";
import SingleSelect from "../../Components/common/SingleSelect";
import Button from "../../Components/common/Button";
import { setCities } from "../../Actions/Cities";
import { nFormatter } from "../../Helpers";
import { currencySymbolHtml } from "../../Components/common/CurrencySymbol";
import { dashMainViewScrollbarStyles } from "../shared/dashboardEnterprise";
import "./salesKpiDashboard.scss";

const getUserTimezone = (logInUser) =>
  logInUser?.attributes?.timezone || "Asia/Riyadh";

const redirectToLogin = () => {
  window.location.replace("/login");
};

const buildCityQuery = (garageCityId) =>
  garageCityId ? `&garageCityId=${encodeURIComponent(garageCityId)}` : "";

const buildamUserIdQuery = (amUserId) =>
  amUserId ? `&amUserId=${encodeURIComponent(amUserId)}` : "";

const getCityLabel = (city) =>
  city?.cityEn || city?.name || city?.region || city?.cityAr || city?.id;

const extractAmOptions = (data) => {
  let list = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (data && typeof data === "object") {
    const nested = pick(
      data,
      "managers",
      "items",
      "scorecard",
      "data",
      "accountManagers",
    );
    if (Array.isArray(nested)) {
      list = nested;
    } else {
      list = Object.values(data).filter(
        (item) => item && typeof item === "object" && !Array.isArray(item),
      );
    }
  }

  const seen = new Set();
  return list.reduce((acc, item) => {
    const id = pick(item, "accountManagerId", "amUserId", "id");
    const name = String(
      pick(item, "name", "accountManagerName", "amName", "accountManager") ||
        "",
    ).trim();
    if (id == null || id === "" || !name || seen.has(String(id))) return acc;
    seen.add(String(id));
    acc.push({
      id,
      name,
      accountManagerId: id,
    });
    return acc;
  }, []);
};

const fetchNskpi = async (path) => {
  const response = await fetch(path, {
    headers: { Accept: "application/json" },
  });
  const json = await response.json();

  if (
    json?.statusCode === "440" ||
    json?.statusCode === 401 ||
    response.status === 401
  ) {
    redirectToLogin();
    throw new Error("SESSION_EXPIRED");
  }

  if (json?.statusCode && json?.status !== "success") {
    const message = String(json?.message || "").toLowerCase();
    if (message.includes("unauthorized") || message.includes("session")) {
      redirectToLogin();
      throw new Error("SESSION_EXPIRED");
    }
  }

  if (json?.status === "success") return json.data ?? json;
  return json?.data ?? json;
};

const COLORS = {
  green: "#22c55e",
  red: "#ef4444",
  orange: "#f59e0b",
  blue: "#3b82f6",
  teal: "#14b8a6",
  muted: "#64748b",
  grid: "#e2e8f0",
  text: "#1e293b",
  tooltipBg: "#1e293b",
};

const darkTooltip = {
  backgroundColor: COLORS.tooltipBg,
  borderColor: COLORS.tooltipBg,
  borderRadius: 8,
  shadow: true,
  style: { color: "#f8fafc", fontSize: "12px" },
};

const extractSparklinePoints = (data) => {
  const fromWeeks = normalizeWeeklyChart(data).revenue || [];
  if (fromWeeks.length) return fromWeeks;

  if (Array.isArray(data)) {
    return data.map((item) =>
      num(item?.totalSaleNet ?? item?.revenue ?? item?.value ?? item),
    );
  }

  const nested = data?.weeks || data?.revenue || data?.items || data?.data;
  if (Array.isArray(nested)) {
    return nested.map((item) =>
      num(item?.totalSaleNet ?? item?.revenue ?? item?.value ?? item),
    );
  }

  return [];
};

const Sparkline = ({ data = [] }) => {
  const points = data.length > 1 ? data : data.length === 1 ? [data[0], data[0]] : [0, 0, 0, 0, 0];
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const width = 52;
  const height = 22;
  const step = width / Math.max(points.length - 1, 1);

  const coords = points.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return { x, y };
  });

  const line = coords
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L${width},${height} L0,${height} Z`;

  return (
    <svg
      className="skd-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <path d={area} fill="rgba(255,255,255,0.18)" />
      <path
        d={line}
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const momChangePercent = (current, previous) => {
  const cur = num(current);
  const prev = num(previous);
  if (prev > 0) return ((cur - prev) / prev) * 100;
  return cur > 0 ? 100 : 0;
};

const MONTH_TARGET_KEYS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const monthTargetKey = (m) => MONTH_TARGET_KEYS[m.month()] || "Jan";

const monthlyTargetFromMap = (monthlyMap, m) =>
  num(monthlyMap?.[monthTargetKey(m)]);

/** Weekly target = that month's target prorated to 7 days. */
const weeklyTargetFromMonth = (monthlyMap, m) => {
  const monthly = monthlyTargetFromMap(monthlyMap, m);
  const days = m.daysInMonth() || 30;
  return (monthly * 7) / days;
};

/**
 * Targets from account (ServerSetting): yearlyRevenueTarget + monthlyRevenueTargets.
 * Weekly is derived from the month covering that week.
 */
const resolveAccountRevenueTargets = (
  account = {},
  period,
  timezone = "Asia/Riyadh",
) => {
  const yearly = num(account?.yearlyRevenueTarget);
  const monthlyMap = account?.monthlyRevenueTargets || {};
  const now = moment().tz(timezone);

  if (period === PERIOD_YTD) {
    return {
      target: yearly,
      targetPrevious: yearly,
    };
  }

  if (period === PERIOD_MTD) {
    return {
      target: monthlyTargetFromMap(monthlyMap, now),
      targetPrevious: monthlyTargetFromMap(
        monthlyMap,
        now.clone().subtract(1, "month"),
      ),
    };
  }

  const weekRange = getIsoWeekRange(period, timezone);
  const weekMid = weekRange.start.clone().add(3, "days");
  const prevWeekMid = weekMid.clone().subtract(1, "week");
  return {
    target: weeklyTargetFromMonth(monthlyMap, weekMid),
    targetPrevious: weeklyTargetFromMonth(monthlyMap, prevWeekMid),
  };
};

const normalizeMtdRevenue = (data = {}, accountTargets = null) => {
  const dnsSale = num(data.monthlySaleCurrent);
  const dnsSalePrevious = num(data.monthlySalePrevious);
  const hasMI =
    data.currentMonthSalesMI != null || data.prevMonthSalesMI != null;
  const manualInvoiceSale = num(data.currentMonthSalesMI);
  const manualInvoiceSalePrevious = num(data.prevMonthSalesMI);

  // Total = delivery notes + manual invoices
  const revenue = dnsSale + manualInvoiceSale;
  const prevMonthRevenue = dnsSalePrevious + manualInvoiceSalePrevious;

  const accountTarget = num(accountTargets?.target);
  const accountTargetPrevious = num(accountTargets?.targetPrevious);
  // Prefer account/ServerSetting targets; fall back to API values
  const target =
    accountTarget > 0 ? accountTarget : num(data.monthlyTargetCurrent);
  const targetPrevious =
    accountTargetPrevious > 0
      ? accountTargetPrevious
      : num(data.monthlyTargetPrevious);

  const momPercent = momChangePercent(revenue, prevMonthRevenue);
  const dnsMomPercent = momChangePercent(dnsSale, dnsSalePrevious);
  const miMomPercent = momChangePercent(
    manualInvoiceSale,
    manualInvoiceSalePrevious,
  );
  const targetPercent = target > 0 ? (revenue / target) * 100 : 0;
  const prevTargetPercent =
    targetPrevious > 0 ? (prevMonthRevenue / targetPrevious) * 100 : null;

  return {
    revenue,
    dnsSale,
    dnsSalePrevious,
    dnsMomPercent,
    hasMI,
    manualInvoiceSale,
    manualInvoiceSalePrevious,
    miMomPercent,
    momPercent,
    prevMonthRevenue,
    target,
    targetPrevious,
    targetPercent,
    prevTargetPercent,
  };
};

const normalizeFleetThroughput = (data = {}) => {
  const totalVisits = num(data.totalDeliveryNotesCount);
  const totalUnits = num(data.totalVehicles);
  const totalServedVehicles = num(data.totalServedVehicles);
  const visitsPerVehicle =
    totalUnits > 0 ? totalVisits / totalServedVehicles : 0;

  return {
    visitsPerVehicle,
    totalVisits,
    totalUnits,
    totalServedVehicles,
  };
};

const normalizeActiveFleet = (data = {}) => ({
  activeFleet: num(data.totalVehicles),
  newVehicles: num(data.newVehicles),
  newAccounts: num(data.newAccounts),
  totalAccounts: num(data.totalAccounts),
  oldVehicles: num(data.oldVehicles),
  oldAccounts: num(data.oldAccounts),
});

const normalizePmsConversion = (data = {}) => ({
  rate: num(data.pmCompliancePercent),
  completed: num(data.earlyCompleted)+num(data.completedPredictionsBeforeToday)+num(data.lateCompleted),
  scheduled: num(data.totalPredictionsBeforeToday),
  onTimeCompleted:num(data.completedPredictionsBeforeToday)
});

const normalizeFleetAtRisk = (data = {}) => {
  let clients = [];

  if (Array.isArray(data)) {
    clients = data.map((item) => ({
      accountName:
        String(
          pick(item, "accountName", "clientName", "contractNumber", "name") ||
            "—",
        ).trim() || "—",
      accountManagerName: String(
        pick(item, "accountManagerName", "amName", "accountManager", "am") ||
          "",
      ).trim(),
      contractNumber: pick(item, "contractNumber") || "",
      vehicleCount: num(
        pick(
          item,
          "riskyVehicleCount",
          "vehicleCount",
          "unvisitedCount",
          "count",
          "vehicles",
        ),
      ),
    }));
  } else {
    clients = Object.entries(data || {}).map(([key, item]) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return {
          accountName:
            String(
              pick(
                item,
                "accountName",
                "clientName",
                "contractNumber",
                "name",
              ) || key,
            ).trim() || key,
          accountManagerName: String(
            pick(
              item,
              "accountManagerName",
              "amName",
              "accountManager",
              "am",
            ) || "",
          ).trim(),
          contractNumber: pick(item, "contractNumber") || key,
          vehicleCount: num(
            pick(
              item,
              "riskyVehicleCount",
              "vehicleCount",
              "unvisitedCount",
              "count",
              "vehicles",
            ),
          ),
        };
      }

      return {
        accountName: key,
        accountManagerName: "",
        contractNumber: key,
        vehicleCount: Array.isArray(item) ? item.length : 0,
      };
    });
  }

  clients.sort((a, b) => b.vehicleCount - a.vehicleCount);

  return {
    unvisitedCount: clients.reduce((sum, item) => sum + item.vehicleCount, 0),
    clients,
  };
};

const formatWeekAxisLabel = (weekFrom, weekTo, timezone) => {
  if (!weekFrom || !weekTo) return "";
  const from = moment.tz(weekFrom, timezone);
  const to = moment.tz(weekTo, timezone);
  if (!from.isValid() || !to.isValid()) return "";
  return `${from.format("DD MMM")} - ${to.format("DD MMM")}`;
};

const normalizeWeeklyChart = (
  data = {},
  fleetTotalVehicles = 0,
  timezone = "Asia/Riyadh",
) => {
  const weekEntries = Object.entries(data || {})
    .filter(([key]) => /^week\d+$/i.test(key))
    .map(([key, value]) => ({
      weekNum: parseInt(key.replace(/\D/g, ""), 10),
      weekFrom: value?.weekFrom,
      weekTo: value?.weekTo,
      totalSaleNet: num(value?.totalSaleNet),
      distinctVehicles: num(value?.distinctVehicles),
      totalDns: num(value?.totalDns),
    }))
    .sort((a, b) => {
      if (a.weekFrom && b.weekFrom) {
        return (
          moment.tz(a.weekFrom, timezone).valueOf() -
          moment.tz(b.weekFrom, timezone).valueOf()
        );
      }
      return a.weekNum - b.weekNum;
    });

  // Most recent 5 weeks, oldest → newest on the axis.
  const recentWeeks = weekEntries.slice(-5);
  const categories = [];
  const revenue = [];
  const activation = [];

  recentWeeks.forEach((week) => {
    const label = formatWeekAxisLabel(week.weekFrom, week.weekTo, timezone);
    categories.push(label || `Week ${week.weekNum}`);
    revenue.push(week.totalSaleNet);
    activation.push(
      fleetTotalVehicles > 0
        ? (week.distinctVehicles / fleetTotalVehicles) * 100
        : 0,
    );
  });

  return { categories, revenue, activation };
};

const getPenetrationTone = (value) => {
  if (value >= 60) return "high";
  if (value >= 35) return "mid";
  return "low";
};

const flattenPenetrationByType = (typeData = {}) => {
  const cityMap = new Map();

  Object.values(typeData).forEach((entries) => {
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      const name = entry?.cityName || "—";
      const count = num(entry?.countOfDn);
      cityMap.set(name, (cityMap.get(name) || 0) + count);
    });
  });

  const cities = Array.from(cityMap.entries())
    .map(([name, count]) => ({ name, count }))
    .filter((item) => item.name !== "—" || item.count > 0)
    .sort((a, b) => b.count - a.count);

  const maxCount =
    cities.reduce((max, city) => Math.max(max, city.count), 0) || 1;

  return cities.map((city) => ({
    ...city,
    percentage: (city.count / maxCount) * 100,
  }));
};

const normalizeFleetPenetration = (data = {}) => {
  if (data?.vehicle || data?.garage) {
    return {
      vehicle: flattenPenetrationByType(data.vehicle),
      garage: flattenPenetrationByType(data.garage),
    };
  }

  const legacyList = flattenPenetrationByType(
    Array.isArray(data)
      ? { legacy: data }
      : pick(data, "regions", "items", "penetration") || {},
  );

  return {
    vehicle: legacyList,
    garage: [],
  };
};

const normalizeScorecard = (data = {}) => {
  let list = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (data && typeof data === "object") {
    list =
      pick(data, "managers", "items", "scorecard", "data", "accountManagers") ||
      [];
  }

  return list
    .map((item, idx) => ({
      rank: num(pick(item, "rank", "position")) || idx + 1,
      name:
        pick(
          item,
          "accountManagerName",
          "name",
          "amName",
          "accountManager",
          "managerName",
          "label",
        ) || "—",
      newAccounts: num(
        pick(
          item,
          "newAccounts",
          "newAccountCount",
          "accounts",
          "newAccountsCount",
        ),
      ),
      newVehicles: num(
        pick(
          item,
          "newVehicles",
          "newVehicleCount",
          "vehicles",
          "newVehiclesCount",
        ),
      ),
      outstanding: num(
        pick(item, "outstanding", "totalOutstanding", "outstandingAmount"),
      ),
    }))
    .sort((a, b) => a.rank - b.rank);
};

const getOutstandingTone = (amount, maxAmount) => {
  if (maxAmount <= 0) return "mid";
  const ratio = amount / maxAmount;
  if (ratio >= 0.9) return "high";
  if (ratio >= 0.65) return "mid";
  return "low";
};

const KpiCardSkeleton = () => (
  <div className="skd-card skd-card--skeleton">
    <Skeleton
      variant="text"
      width="60%"
      height={14}
      sx={{ bgcolor: "#e2e8f0" }}
    />
    <Skeleton
      variant="text"
      width="80%"
      height={22}
      sx={{ bgcolor: "#e2e8f0", mt: 0.5 }}
    />
    <Skeleton
      variant="text"
      width="70%"
      height={14}
      sx={{ bgcolor: "#e2e8f0" }}
    />
  </div>
);

const MomBadge = ({ percent }) => {
  const p = num(percent);
  // Always: up = green, down = red
  const tone = p > 0 ? "is-good" : p < 0 ? "is-bad" : "is-flat";
  const arrow = p > 0 ? "▲" : p < 0 ? "▼" : "■";
  return (
    <span className={`skd-mom-badge ${tone}`}>
      {arrow} {Math.abs(p).toFixed(1)}%
    </span>
  );
};

const KpiCard = ({
  label,
  info,
  value,
  valueTitle,
  unit,
  afterValue,
  sub,
  subTitle,
  footer,
  footerTitle,
  variant,
  progress,
  progressMarker,
  progressMarkerTitle,
  sparklineData,
  loading,
  className,
}) => {
  if (loading) return <KpiCardSkeleton />;
  const bar = Math.min(num(progress), 100);
  const marker =
    progressMarker == null || Number.isNaN(num(progressMarker))
      ? null
      : Math.min(num(progressMarker), 100);
  return (
    <div
      className={`skd-card${variant ? ` skd-card--${variant}` : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      {sparklineData ? <Sparkline data={sparklineData} /> : null}
      <div className="skd-card-label">
        <span className="skd-card-label-text">{label}</span>
        {info ? (
          <Tip title={info} className="skd-card-info" component="span">
            <InfoOutlinedIcon className="skd-card-info-icon" />
          </Tip>
        ) : null}
      </div>
      <div className="skd-card-main">
        <div className="skd-card-value-row">
          <Tip title={valueTitle} className="skd-card-value" component="div">
            {value}
          </Tip>
          {unit ? <span className="skd-card-unit">{unit}</span> : null}
          {afterValue ? (
            <div className="skd-card-after-value">{afterValue}</div>
          ) : null}
        </div>
      </div>
      <Tip title={subTitle} className="skd-card-sub" component="div">
        {sub || "\u00A0"}
      </Tip>
      <div className="skd-card-bottom">
        <div className="skd-progress-track">
          <div className="skd-progress-fill" style={{ width: `${bar}%` }} />
          {marker != null ? (
            <span
              className="skd-progress-marker"
              style={{ left: `calc(${marker}% - 1px)` }}
              title={progressMarkerTitle}
            />
          ) : null}
        </div>
        <Tip title={footerTitle} className="skd-card-footer" component="div">
          {footer || "\u00A0"}
        </Tip>
      </div>
    </div>
  );
};

const SalesKPIDashboard = ({
  logInUser,
  ServerSetting,
  translate,
  cities,
  userCities,
}) => {
  const dispatch = useDispatch();
  const currency = logInUser?.attributes?.currencyLabel || "AED";
  const contractType = useMemo(
    () =>
      ServerSetting?.contractType ||
      logInUser?.contractType ||
      logInUser?.attributes?.contractType ||
      1,
    [
      ServerSetting?.contractType,
      logInUser?.contractType,
      logInUser?.attributes?.contractType,
    ],
  );
  const t = useCallback(
    (key) => (translate ? translate(key) : key),
    [translate],
  );

  const [loading, setLoading] = useState({
    mtd: true,
    throughput: true,
    activeFleet: true,
    pms: true,
    fleetAtRisk: true,
    kpiOverdue: true,
    fleetOverdue: true,
    weekly: true,
    productMix: true,
    fleetPenetration: true,
    scorecard: true,
    fleetMakeModel: true,
  });

  const [mtdRevenue, setMtdRevenue] = useState({});
  const [fleetThroughput, setFleetThroughput] = useState({});
  const [activeFleet, setActiveFleet] = useState({});
  const [pmsConversion, setPmsConversion] = useState({});
  const [fleetAtRisk, setFleetAtRisk] = useState({});
  const [kpiOverdue, setKpiOverdue] = useState([]);
  const [kpiSparkline, setKpiSparkline] = useState([]);
  const [fleetOverdue, setFleetOverdue] = useState([]);
  const [weeklyChartData, setWeeklyChartData] = useState({});
  const [productMix, setProductMix] = useState({});
  const [fleetPenetration, setFleetPenetration] = useState({});
  const [scorecard, setScorecard] = useState([]);
  const [fleetMakeModel, setFleetMakeModel] = useState({});
  const [penetrationView, setPenetrationView] = useState("vehicle");
  const [scorecardSearch, setScorecardSearch] = useState("");
  const [riskSearch, setRiskSearch] = useState("");
  const [timePeriod, setTimePeriod] = useState("weekly");
  const timezone = getUserTimezone(logInUser);
  const ytdDateRange = useMemo(() => getYtdDateRange(timezone), [timezone]);
  // First load matches previous behaviour: YTD.
  const defaultPeriod = PERIOD_YTD;
  const defaultPeriodRange = useMemo(
    () => getPeriodDateRange(defaultPeriod, timezone),
    [timezone],
  );
  const [selectedWeek, setSelectedWeek] = useState(defaultPeriod);
  const [dateRange, setDateRange] = useState(defaultPeriodRange);
  const [selectedgarageCityId, setSelectedgarageCityId] = useState("");
  const [selectedamUserId, setSelectedamUserId] = useState("");
  const [amList, setAmList] = useState([]);
  const citiesFetchStarted = useRef(false);
  const arChartWrapRef = useRef(null);
  const arChartRef = useRef(null);

  const cityOptions = useMemo(() => {
    const list = userCities?.length ? userCities : cities || [];
    return list.map((city) => ({
      ...city,
      name: getCityLabel(city),
    }));
  }, [cities, userCities]);

  const selectedCityValue = useMemo(() => {
    const city = cityOptions.find(
      (item) => String(item.id) === String(selectedgarageCityId),
    );
    if (!city) return "";
    const label = getCityLabel(city);
    return {
      id: city.id,
      value: label,
      label,
      name: label,
    };
  }, [cityOptions, selectedgarageCityId]);

  const handleCityChange = useCallback((_name, value) => {
    setSelectedgarageCityId(value?.id != null ? String(value.id) : "");
  }, []);

  const amOptions = useMemo(() => extractAmOptions(amList), [amList]);

  const selectedAmValue = useMemo(() => {
    const am = amOptions.find(
      (item) => String(item.accountManagerId) === String(selectedamUserId),
    );
    if (!am) return "";
    return {
      id: am.accountManagerId,
      value: am.name,
      label: am.name,
      name: am.name,
      accountManagerId: am.accountManagerId,
    };
  }, [amOptions, selectedamUserId]);

  const handleWeekChange = useCallback(
    (period) => {
      setSelectedWeek(period);
      const { from, to } = getPeriodDateRange(period, timezone);
      setDateRange((prev) => {
        if (prev.from === from && prev.to === to) return prev;
        return { from, to };
      });
    },
    [timezone],
  );

  const runRequests = useCallback(async (requests) => {
    await Promise.all(
      requests.map(async ({ key, url, setter }) => {
        setLoading((prev) => ({ ...prev, [key]: true }));
        try {
          const data = await fetchNskpi(url);
          setter(data);
        } catch (e) {
          if (e?.message === "SESSION_EXPIRED") return;
          console.error(`Sales KPI fetch failed: ${key}`, e);
        } finally {
          setLoading((prev) => ({ ...prev, [key]: false }));
        }
      }),
    );
  }, []);

  const loadTopRowWidgets = useCallback(
    async (amUserId = "", range = null, period = PERIOD_YTD) => {
      const periodRange = range?.from && range?.to ? range : ytdDateRange;
      if (!contractType || !periodRange?.from || !periodRange?.to) return;

      const { from, to } = periodRange;
      const mtdDates = getMtdRevenueCompareRanges(period, timezone);
      const base = `/api/nskpi`;
      const ct = `contractType=${contractType}`;
      const dateQuery = `to=${to}&from=${from}`;
      const mtdDateQuery = [
        `curMonthStart=${encodeURIComponent(mtdDates.curMonthStart)}`,
        `curMonthEnd=${encodeURIComponent(mtdDates.curMonthEnd)}`,
        `prevMonthStart=${encodeURIComponent(mtdDates.prevMonthStart)}`,
        `prevMonthEnd=${encodeURIComponent(mtdDates.prevMonthEnd)}`,
      ].join("&");
      const amUserIdQuery = buildamUserIdQuery(amUserId);

      await runRequests([
        {
          key: "mtd",
          url: `${base}/mtd_revenue/?${ct}&${mtdDateQuery}${amUserIdQuery}`,
          setter: (d) => setMtdRevenue(d),
        },
        {
          key: "throughput",
          url: `${base}/fleet_throughput/?${ct}${amUserIdQuery}`,
          setter: (d) => setFleetThroughput(d),
        },
        {
          key: "activeFleet",
          url: `${base}/active_fleet_and_expansion/?${ct}${amUserIdQuery}`,
          setter: (d) => setActiveFleet(d),
        },
        {
          key: "pms",
          url: `${base}/pms_conversion_rate/?${ct}${amUserIdQuery}`,
          setter: (d) => setPmsConversion(d),
        },
        {
          key: "fleetAtRisk",
          url: `${base}/fleet_at_risk/?${ct}${amUserIdQuery}`,
          setter: (d) => setFleetAtRisk(d),
        },
        {
          key: "kpiOverdue",
          url: `${base}/fleet_and_overdue?${ct}&${dateQuery}&userType=3${amUserIdQuery}`,
          setter: (d) => setKpiOverdue(d),
        },
      ]);
    },
    [contractType, timezone, ytdDateRange, runRequests],
  );

  const loadStaticWidgets = useCallback(async () => {
    if (!contractType) return;

    const base = `/api/nskpi`;
    const ct = `contractType=${contractType}`;

    await Promise.all([
      loadTopRowWidgets(""),
      runRequests([
        {
          key: "productMix",
          url: `${base}/product_mix_and_pms_conversion/?${ct}`,
          setter: (d) => setProductMix(d),
        },
        {
          key: "scorecard",
          url: `${base}/scorecard?${ct}`,
          setter: (d) => {
            setScorecard(d);
            setAmList((prev) => (extractAmOptions(prev).length ? prev : d));
          },
        },
      ]),
    ]);
  }, [contractType, loadTopRowWidgets, runRequests]);

  const loadDateFilteredWidgets = useCallback(
    async (range, garageCityId = "", amUserId = "") => {
      if (!contractType || !range?.from || !range?.to) return;

      const { from, to } = range;
      const toToday = moment().tz(timezone).endOf("day").toISOString();
      const base = `/api/nskpi`;
      const ct = `contractType=${contractType}`;
      const dateQuery = `to=${to}&from=${from}`;
      const makeModelDateQuery = `to=${encodeURIComponent(toToday)}&from=${encodeURIComponent(from)}`;
      const cityQuery = buildCityQuery(garageCityId);
      const amUserIdQuery = buildamUserIdQuery(amUserId);

      await runRequests([
        {
          key: "fleetOverdue",
          url: `${base}/fleet_and_overdue?${ct}&${dateQuery}&userType=3${cityQuery}${amUserIdQuery}`,
          setter: (d) => setFleetOverdue(d),
        },
        {
          key: "weekly",
          url: `${base}/weekly_revenue_vs_activation_rate?${ct}&${dateQuery}${cityQuery}${amUserIdQuery}`,
          setter: (d) => {
            setWeeklyChartData(d);
            setKpiSparkline((prev) =>
              prev?.length ? prev : extractSparklinePoints(d),
            );
          },
        },
        {
          key: "fleetPenetration",
          url: `${base}/fleet_penetration?${ct}&${dateQuery}${cityQuery}${amUserIdQuery}`,
          setter: (d) => setFleetPenetration(d),
        },
        {
          key: "fleetMakeModel",
          url: `${base}/dn_count_by_model/?${ct}&${makeModelDateQuery}${cityQuery}${amUserIdQuery}`,
          setter: (d) => setFleetMakeModel(d),
        },
      ]);
    },
    [contractType, runRequests, timezone],
  );

  const loadScorecard = useCallback(
    async (amUserId = "") => {
      if (!contractType) return;
      const ct = `contractType=${contractType}`;
      const amUserIdQuery = buildamUserIdQuery(amUserId);
      await runRequests([
        {
          key: "scorecard",
          url: `/api/nskpi/scorecard?${ct}${amUserIdQuery}`,
          setter: (d) => setScorecard(d),
        },
        {
          key: "productMix",
          url: `/api/nskpi/product_mix_and_pms_conversion/?${ct}${amUserIdQuery}`,
          setter: (d) => setProductMix(d),
        },
      ]);
    },
    [contractType, runRequests],
  );

  const handleAmChange = useCallback((_name, value) => {
    const amUserId =
      value?.accountManagerId != null
        ? String(value.accountManagerId)
        : value?.id != null
          ? String(value.id)
          : "";
    setSelectedamUserId(amUserId);
  }, []);

  const isResetEnabled = useMemo(() => {
    const dateChanged =
      dateRange.from !== defaultPeriodRange.from ||
      dateRange.to !== defaultPeriodRange.to;
    return (
      Boolean(selectedgarageCityId) ||
      Boolean(selectedamUserId) ||
      dateChanged ||
      selectedWeek !== defaultPeriod
    );
  }, [
    dateRange,
    defaultPeriod,
    defaultPeriodRange,
    selectedamUserId,
    selectedgarageCityId,
    selectedWeek,
  ]);

  const handleSearch = useCallback(() => {
    // Use whatever the picker shows: YTD / MTD / week.
    const periodRange = getPeriodDateRange(selectedWeek, timezone);
    setDateRange(periodRange);

    // Always refresh so clearing AM / city also reloads unfiltered data.
    loadTopRowWidgets(selectedamUserId || "", periodRange, selectedWeek);
    loadScorecard(selectedamUserId || "");
    loadDateFilteredWidgets(
      periodRange,
      selectedgarageCityId,
      selectedamUserId || "",
    );
  }, [
    loadDateFilteredWidgets,
    loadScorecard,
    loadTopRowWidgets,
    selectedWeek,
    selectedamUserId,
    selectedgarageCityId,
    timezone,
  ]);

  const handleReset = useCallback(() => {
    setSelectedgarageCityId("");
    setSelectedamUserId("");
    setSelectedWeek(defaultPeriod);
    setDateRange(defaultPeriodRange);
    loadTopRowWidgets("", defaultPeriodRange, defaultPeriod);
    loadScorecard("");
    loadDateFilteredWidgets(defaultPeriodRange, "", "");
  }, [
    defaultPeriod,
    defaultPeriodRange,
    loadDateFilteredWidgets,
    loadScorecard,
    loadTopRowWidgets,
  ]);

  useEffect(() => {
    if (cities?.length || userCities?.length || citiesFetchStarted.current) {
      return undefined;
    }
    citiesFetchStarted.current = true;
    let cancelled = false;
    fetch(`/api/citys/get?limit=-1`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => response.json())
      .then((res) => {
        if (cancelled) return;
        if (res?.statusCode === "440" || res?.statusCode === 401) {
          redirectToLogin();
          return;
        }
        if (res?.status === "success") {
          const list = (res.data?.data || []).map((item) => ({
            ...item,
            name: item?.cityEn || item?.region,
          }));
          dispatch(setCities(list));
        }
      })
      .catch((e) => {
        console.error("Sales KPI cities fetch failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [cities, userCities, dispatch]);

  useEffect(() => {
    loadStaticWidgets();
    loadDateFilteredWidgets(defaultPeriodRange, "");
  }, [defaultPeriodRange, loadDateFilteredWidgets, loadStaticWidgets]);

  const throughput = normalizeFleetThroughput(fleetThroughput);
  const weeklyChart = useMemo(
    () =>
      normalizeWeeklyChart(weeklyChartData, throughput.totalUnits, timezone),
    [weeklyChartData, throughput.totalUnits, timezone],
  );

  const weeklyChartOptions = useMemo(
    () => ({
      chart: { type: "column", backgroundColor: "transparent", height: 340 },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: weeklyChart.categories,
        labels: { style: { color: COLORS.muted, fontSize: "11px" } },
        lineColor: COLORS.grid,
        tickColor: COLORS.grid,
      },
      yAxis: [
        {
          title: { text: null },
          labels: {
            style: { color: COLORS.muted, fontSize: "11px" },
            formatter() {
              return nFormatter(this.value, 1);
            },
          },
          gridLineColor: COLORS.grid,
        },
        {
          title: { text: null },
          labels: {
            format: "{value}%",
            style: { color: COLORS.muted, fontSize: "11px" },
          },
          opposite: true,
          gridLineColor: "transparent",
        },
      ],
      legend: {
        useHTML: true,
        align: "center",
        itemStyle: { color: COLORS.text, fontSize: "11px", fontWeight: "600" },
        itemHoverStyle: { color: COLORS.text },
        symbolRadius: 6,
      },
      tooltip: {
        ...darkTooltip,
        shared: true,
        useHTML: true,
        formatter() {
          const header = `<span style="font-size:11px;font-weight:700">${this.x}</span><br/>`;
          const rows = (this.points || []).map((point) => {
            const suffix =
              point.series.type === "column"
                ? ` ${currencySymbolHtml(currency)}`
                : "";
            return `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y, 1)}</b>${suffix}<br/>`;
          });
          return header + rows.join("");
        },
      },
      plotOptions: {
        column: {
          borderRadius: 6,
          borderWidth: 0,
          color: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "#67e8f9"],
              [1, "#22d3ee"],
            ],
          },
          pointPadding: 0.12,
          groupPadding: 0.08,
        },
        areaspline: {
          fillOpacity: 0.12,
          lineWidth: 2.5,
          marker: {
            enabled: true,
            radius: 5,
            fillColor: COLORS.green,
            lineColor: "#fff",
            lineWidth: 2,
          },
        },
      },
      series: [
        {
          name: t("skdWeeklyRevenue"),
          type: "column",
          yAxis: 0,
          data: weeklyChart.revenue,
        },
        {
          name: t("skdActivationRate"),
          type: "areaspline",
          yAxis: 1,
          color: COLORS.green,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "rgba(34,197,94,0.25)"],
              [1, "rgba(34,197,94,0.02)"],
            ],
          },
          data: weeklyChart.activation,
        },
      ],
    }),
    [weeklyChart, t, currency],
  );

  const overdue = normalizeFleetAndOverdue(fleetOverdue);
  const kpiOutstanding = normalizeFleetAndOverdue(kpiOverdue);

  const arBreakdownChartOptions = useMemo(() => {
    const collected = overdue.totalCollected;
    const overdueAmount = overdue.totalOverdue;
    const outstanding = overdue.totalOutstanding;
    const hasData = collected + overdueAmount + outstanding > 0;

    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 180,
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
      },
      title: { text: null },
      credits: { enabled: false },
      tooltip: {
        ...darkTooltip,
        pointFormat: "<b>{point.name}</b>: {point.y:,.0f}",
      },
      legend: { enabled: false },
      plotOptions: {
        pie: {
          innerSize: "58%",
          borderWidth: 3,
          borderColor: "#fff",
          center: ["50%", "50%"],
          size: "80%",
          dataLabels: { enabled: false },
          showInLegend: false,
          states: { hover: { halo: { size: 6 } } },
        },
      },
      series: [
        {
          name: t("skdArBreakdown"),
          data: hasData
            ? [
                { name: t("skdCollected"), y: collected, color: "#22c55e" },
                { name: t("skdOverdue"), y: overdueAmount, color: "#ef4444" },
                { name: t("skdOutstanding"), y: outstanding, color: "#f59e0b" },
              ]
            : [],
        },
      ],
    };
  }, [overdue, t]);

  useEffect(() => {
    const wrap = arChartWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return undefined;

    const reflowChart = () => {
      arChartRef.current?.chart?.reflow();
    };

    const observer = new ResizeObserver(reflowChart);
    observer.observe(wrap);
    reflowChart();

    return () => observer.disconnect();
  }, [arBreakdownChartOptions]);

  const accountRevenueTargets = useMemo(
    () =>
      resolveAccountRevenueTargets(
        ServerSetting || logInUser,
        selectedWeek,
        timezone,
      ),
    [ServerSetting, logInUser, selectedWeek, timezone],
  );
  const mtd = useMemo(
    () => normalizeMtdRevenue(mtdRevenue, accountRevenueTargets),
    [mtdRevenue, accountRevenueTargets],
  );
  const revenuePeriodYear = useMemo(
    () => moment().tz(timezone).year(),
    [timezone],
  );
  // Same style as before ("MTD Revenue & Target"), period from dropdown
  const mtdRevenueLabel = useMemo(() => {
    let period = t("skdMtd");
    if (selectedWeek === PERIOD_YTD) {
      period = `${t("skdYtd")} ${revenuePeriodYear}`;
    } else if (selectedWeek === PERIOD_MTD) {
      period = t("skdMtd");
    } else {
      const weekNum = parseInt(selectedWeek, 10);
      period = Number.isFinite(weekNum)
        ? `${t("skdWeek")} ${weekNum}`
        : `${t("skdWeek")} ${selectedWeek}`;
    }
    const base = String(t("skdMtdRevenue"));
    if (/MTD|YTD/i.test(base)) {
      return base.replace(/MTD|YTD/i, period);
    }
    return `${period} ${base}`;
  }, [selectedWeek, t, revenuePeriodYear]);

  const revenuePeriodLabels = useMemo(() => {
    if (selectedWeek === PERIOD_YTD) {
      return {
        thisPeriod: t("skdThisYear"),
        prevPeriod: t("skdPrevYear"),
        lastPeriodShort: t("skdLastYear"),
        noPrevData: t("skdNoPrevYearData"),
        mom: "YoY",
      };
    }
    if (selectedWeek === PERIOD_MTD) {
      return {
        thisPeriod: t("skdThisMonth"),
        prevPeriod: t("skdPrevMonth"),
        lastPeriodShort: t("skdLastMonth"),
        noPrevData: t("skdNoPrevMonthData"),
        mom: "MoM",
      };
    }
    return {
      thisPeriod: t("skdThisWeek"),
      prevPeriod: t("skdPrevWeek"),
      lastPeriodShort: t("skdLastWeek"),
      noPrevData: t("skdNoPrevWeekData"),
      mom: "WoW",
    };
  }, [selectedWeek, t]);
  const fleet = normalizeActiveFleet(activeFleet);
  const pms = normalizePmsConversion(pmsConversion);
  const atRisk = useMemo(
    () => normalizeFleetAtRisk(fleetAtRisk),
    [fleetAtRisk],
  );
  const atRiskClients = useMemo(() => {
    const clients = atRisk.clients || [];
    const q = riskSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((client) => {
      const account = String(client.accountName || "").toLowerCase();
      const am = String(client.accountManagerName || "").toLowerCase();
      const contract = String(client.contractNumber || "").toLowerCase();
      return account.includes(q) || am.includes(q) || contract.includes(q);
    });
  }, [atRisk, riskSearch]);
  const mix = normalizeProductMix(productMix);
  const penetrationData = useMemo(
    () => normalizeFleetPenetration(fleetPenetration),
    [fleetPenetration],
  );
  const penetrationRegions = penetrationData[penetrationView] || [];
  const scorecardRows = useMemo(() => {
    const rows = normalizeScorecard(scorecard);
    const q = scorecardSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      String(row.name || "")
        .toLowerCase()
        .includes(q),
    );
  }, [scorecard, scorecardSearch]);
  const maxScorecardOutstanding = useMemo(
    () =>
      normalizeScorecard(scorecard).reduce(
        (max, row) => Math.max(max, row.outstanding),
        0,
      ),
    [scorecard],
  );

  const pmsRate = pms.rate || mix.conversionRate;
  const targetProgress = mtd.targetPercent;
  const maxRiskVehicles = Math.max(
    ...(atRiskClients.length
      ? atRiskClients.map((c) => c.vehicleCount)
      : [1]),
    1,
  );
  const riskBarPercent = Math.min(
    (atRisk.unvisitedCount / Math.max(throughput.totalUnits, 1)) * 100,
    100,
  );

  return (
    <div className="sales-kpi-dashboard">
      <GlobalStyles styles={dashMainViewScrollbarStyles} />
      <div className="skd-page-header skd-date-filter-bar">
        <h1 className="skd-page-title">{t("skdPageTitle")}</h1>
        <div className="skd-filter-actions">
          <div className="skd-week-filter-wrap">
            <SalesDateRangePicker
              timezone={timezone}
              weekLabel={t("skdWeek")}
              currentWeekLabel={t("skdCurrentWeek")}
              ytdLabel={t("skdYtd")}
              mtdLabel={t("skdMtd")}
              selectedWeek={selectedWeek}
              onWeekChange={handleWeekChange}
            />
          </div>
          <div className="skd-city-filter-wrap">
            <label className="skd-filter-field-label" htmlFor="garageCityId">
              {t("city")}
            </label>
            <SingleSelect
              array={cityOptions}
              async
              selectName="garageCityId"
              isClearable
              label={t("city")}
              value={selectedCityValue}
              handleChange={handleCityChange}
              canAssign
              hidebg
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                container: (base) => ({ ...base, marginTop: 0, marginBottom: 0 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="skd-am-filter-wrap">
            <label className="skd-filter-field-label" htmlFor="amUserId">
              {t("skdAmName")}
            </label>
            <SingleSelect
              array={amOptions}
              async
              selectName="amUserId"
              isClearable
              label={t("skdAmName")}
              value={selectedAmValue}
              handleChange={handleAmChange}
              canAssign
              hidebg
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                container: (base) => ({ ...base, marginTop: 0, marginBottom: 0 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="skd-search-btn-wrap">
            <Button
              aria-label="search"
              size="small"
              color="inherit"
              onClick={handleSearch}
              id="search"
            >
              {t("search")}
            </Button>
          </div>
          <div className="skd-search-btn-wrap">
            <Button
              aria-label="reset"
              size="small"
              color="inherit"
              disabled={!isResetEnabled}
              onClick={handleReset}
              id="reset"
            >
              {t("reset")}
            </Button>
          </div>
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="skd-kpi-grid">
        <KpiCard
          loading={loading.mtd}
          variant="revenue"
          className="skd-card--wide skd-card--revenue-detail"
          label={mtdRevenueLabel}
          info={
            <>
            {`Month-to-date valid delivery notes + mannual invoices compared with the previous month's period (with VAT). The bar shows progress against the monthly target.`}
            </>
          }
          value={formatCurrency(mtd.revenue, currency)}
          valueTitle={fullCurrency(mtd.revenue, currency)}
          sparklineData={
            kpiSparkline.length
              ? kpiSparkline
              : weeklyChart.revenue?.length
                ? weeklyChart.revenue
                : [mtd.prevMonthRevenue, mtd.revenue]
          }
          afterValue={
            <>
              <MomBadge percent={mtd.momPercent} />
              <span className="skd-card-mom-prev">
                {mtd.prevMonthRevenue
                  ? <>vs {formatCurrency(mtd.prevMonthRevenue, currency)}</>
                  : revenuePeriodLabels.noPrevData}
              </span>
            </>
          }
          sub={
            <div className="skd-rev-split">
              <Tip
                className="skd-rev-split-row"
                component="div"
                title={
                  <>
                    {t("deliveryNotes")} ({t("skdDn")}){"\n"}
                    {revenuePeriodLabels.thisPeriod}:{" "}
                    {fullCurrency(mtd.dnsSale, currency)}
                    {"\n"}
                    {revenuePeriodLabels.prevPeriod}:{" "}
                    {fullCurrency(mtd.dnsSalePrevious, currency)}
                    {"\n"}
                    {revenuePeriodLabels.mom}: {fullPct(mtd.dnsMomPercent)}
                  </>
                }
              >
                <span className="skd-rev-badge">{t("skdDn")}</span>
                <b className="skd-rev-cur">
                  {formatCurrency(mtd.dnsSale, currency)}
                </b>
                <span className="skd-rev-vs">
                  vs {formatCurrency(mtd.dnsSalePrevious, currency)}
                </span>
                <MomBadge percent={mtd.dnsMomPercent} />
              </Tip>

              {mtd.hasMI ? (
                <Tip
                  className="skd-rev-split-row"
                  component="div"
                  title={
                    <>
                      {t("manualInvoice")} ({t("skdMi")}){"\n"}
                      {revenuePeriodLabels.thisPeriod}:{" "}
                      {fullCurrency(mtd.manualInvoiceSale, currency)}
                      {"\n"}
                      {revenuePeriodLabels.prevPeriod}:{" "}
                      {fullCurrency(mtd.manualInvoiceSalePrevious, currency)}
                      {"\n"}
                      {revenuePeriodLabels.mom}: {fullPct(mtd.miMomPercent)}
                    </>
                  }
                >
                  <span className="skd-rev-badge">{t("skdMi")}</span>
                  <b className="skd-rev-cur">
                    {formatCurrency(mtd.manualInvoiceSale, currency)}
                  </b>
                  <span className="skd-rev-vs">
                    vs {formatCurrency(mtd.manualInvoiceSalePrevious, currency)}
                  </span>
                  <MomBadge percent={mtd.miMomPercent} />
                </Tip>
              ) : null}
            </div>
          }
          footer={
            <span className="skd-rev-footer">
              <span>
                <span className="skd-rev-ach">{t("skdAchieved")}</span>{" "}
                {formatCurrency(mtd.revenue, currency)}
                {mtd.target ? (
                  <>
                    {" "}
                    <span className="skd-rev-ach">of</span>{" "}
                    {formatCurrency(mtd.target, currency)}
                  </>
                ) : (
                  <> · {t("skdNoTarget")}</>
                )}
              </span>
              {mtd.target ? (
                <em className="skd-rev-footer-pct">{pct(targetProgress, 1)}</em>
              ) : null}
            </span>
          }
          footerTitle={
            <>
              {t("skdAchieved")} {fullCurrency(mtd.revenue, currency)}
              {mtd.target ? (
                <>
                  {" "}
                  of {fullCurrency(mtd.target, currency)} (
                  {fullPct(targetProgress)})
                </>
              ) : null}
              {mtd.prevTargetPercent != null
                ? ` · ${revenuePeriodLabels.lastPeriodShort} ${mtd.prevTargetPercent.toFixed(1)}%`
                : ""}
            </>
          }
          progress={mtd.target ? Math.min(targetProgress, 100) : 0}
          progressMarker={mtd.prevTargetPercent}
          progressMarkerTitle={
            mtd.prevTargetPercent != null
              ? `${revenuePeriodLabels.lastPeriodShort} ${mtd.prevTargetPercent.toFixed(1)}%`
              : undefined
          }
        />

        <KpiCard
          loading={loading.throughput}
          variant="throughput"
          label={t("skdFleetThroughput")}
          info={t("skdFleetThroughputInfo")}
          value={throughput.visitsPerVehicle.toFixed(1)}
          unit={t("skdVisitsPerVehicle")}
          valueTitle={`${fullNumber(throughput.visitsPerVehicle, 2)} ${t("skdVisitsPerVehicle")}`}
          sub={
            <span className="skd-card-meta">
              <span>
                <strong>{nFormatter(throughput.totalVisits, 1)}</strong>{" "}
                {t("skdVisits")}
              </span>
              <span className="skd-throughput-sep">·</span>
              <span>
                <strong>{nFormatter(throughput.totalUnits, 1)}</strong>{" "}
                {t("skdUnits")}
              </span>
            </span>
          }
          subTitle={`${fullNumber(throughput.totalVisits)} ${t("skdVisits")} / ${fullNumber(throughput.totalUnits)} ${t("skdUnits")}`}
          footer={t("skdOptimalFrequency")}
          progress={0}
        />

        <KpiCard
          loading={loading.activeFleet}
          variant="fleet"
          label={t("skdActiveFleetExpansion")}
          info={t("skdActiveFleetExpansionInfo")}
          value={fullNumber(fleet.activeFleet)}
          unit={t("skdVehicles")}
          valueTitle={`${fullNumber(fleet.activeFleet)} ${t("skdVehicles")}`}
          sub={
            <span className="skd-card-meta">
              <span>
                +<strong>{nFormatter(fleet.newVehicles, 1)}</strong>{" "}
                {t("skdVehShort")}
              </span>
              <span className="skd-throughput-sep">·</span>
              <span>
                +<strong>{nFormatter(fleet.newAccounts, 1)}</strong>{" "}
                {t("skdAccShort")}
              </span>
            </span>
          }
          subTitle={[
            `+${fullNumber(fleet.newVehicles)} ${t("skdNewVehicles")}`,
            `+${fullNumber(fleet.newAccounts)} ${t("skdNewAccounts")}`,
          ].join(" · ")}
          footer={
            fleet.totalAccounts
              ? `${t("skdTotalAccounts")}: ${nFormatter(fleet.totalAccounts, 1)}`
              : undefined
          }
          footerTitle={
            fleet.totalAccounts
              ? `${t("skdTotalAccounts")}: ${fullNumber(fleet.totalAccounts)}`
              : undefined
          }
          progress={0}
        />

        <KpiCard
          loading={loading.pms}
          variant="pms"
          label={t("skdPmsConversionRate")}
          info={t("skdPmsConversionRateInfo")}
          value={pct(pmsRate, 1)}
          valueTitle={fullPct(pmsRate)}
          sub={
            pms.completed > 0 || pms.scheduled > 0 ? (
              <span className="skd-card-meta">
                <span>
                  <strong>{nFormatter(pms.completed, 1)}</strong>{" "}
                  {t("skdCompleted")}
                </span>
                <span className="skd-throughput-sep">·</span>
                <span>
                  <strong>{nFormatter(pms.scheduled, 1)}</strong>{" "}
                  {t("skdSched")}
                </span>
              </span>
            ) : null
          }
          subTitle={`${fullNumber(pms.completed)} ${t("skdCompleted")} / ${fullNumber(pms.scheduled)} ${t("skdSched")}`}
          footer={t("skdPreventiveMaintScheduled")}
          progress={Math.min(pmsRate, 100)}
        />

        <KpiCard
          loading={loading.fleetAtRisk}
          variant="risk"
          label={t("skdFleetAtRisk")}
          info={t("skdFleetAtRiskInfo")}
          value={nFormatter(atRisk.unvisitedCount, 1)}
          unit={t("skdVehShort")}
          valueTitle={`${fullNumber(atRisk.unvisitedCount)} ${t("skdUnvisitedVehicles")}`}
          sub={
            <span className="skd-danger">
              <WarningAmberIcon
                sx={{ fontSize: 13, verticalAlign: "middle", mr: 0.5 }}
              />
              {t("skdRequiresAction")}
            </span>
          }
          footer={t("skdUnvisitedVehicles")}
          progress={riskBarPercent}
        />

        <KpiCard
          loading={loading.kpiOverdue}
          variant="outstanding"
          label={t("skdClientOutstanding")}
          info={t("skdClientOutstandingInfo")}
          value={formatCurrency(kpiOutstanding.totalOutstanding, currency)}
          valueTitle={fullCurrency(kpiOutstanding.totalOutstanding, currency)}
          sub={
            kpiOutstanding.totalOverdue > 0 ? (
              <span className="skd-danger">
                {formatCurrency(kpiOutstanding.totalOverdue, currency)}{" "}
                {t("skdOverdueLabel")}
              </span>
            ) : null
          }
          subTitle={
            kpiOutstanding.totalOverdue > 0 ? (
              <>
                {fullCurrency(kpiOutstanding.totalOverdue, currency)}{" "}
                {t("skdOverdueLabel")}
              </>
            ) : undefined
          }
          footer={t("skdAcrossAssignedAccounts")}
          progress={
            kpiOutstanding.totalOutstanding > 0
              ? Math.min(
                  (kpiOutstanding.totalOverdue /
                    kpiOutstanding.totalOutstanding) *
                    100,
                  100,
                )
              : 0
          }
        />
      </div>

      {/* Row 2: Weekly chart + Fleet at risk list */}
      <Grid container spacing={2} className="skd-section-row">
        <Grid item xs={12} lg={8}>
          {loading.weekly ? (
            <WidgetSkeleton height={360} />
          ) : (
            <div className="skd-widget skd-widget--fixed-h-lg">
              <div className="skd-widget-header">
                <div>
                  <div className="skd-widget-title">
                    <span>{t("skdWeeklyRevenueVsActivation")}</span>
                    <WidgetInfo title={t("skdWeeklyRevenueVsActivationInfo")} />
                  </div>
                  <div className="skd-widget-subtitle">
                    {t("skdWeeklyTrajectory")}
                  </div>
                </div>
                {/* <div className="skd-widget-controls">
                  <div className="skd-period-toggle">
                    {["daily", "weekly", "monthly"].map((period) => (
                      <button
                        key={period}
                        type="button"
                        className={timePeriod === period ? "active" : ""}
                        onClick={() => setTimePeriod(period)}
                      >
                        {t(
                          `skdPeriod${period.charAt(0).toUpperCase()}${period.slice(1)}`,
                        )}
                      </button>
                    ))}
                  </div>
                </div> */}
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={weeklyChartOptions}
              />
            </div>
          )}
        </Grid>

        <Grid item xs={12} lg={4}>
          {loading.fleetAtRisk ? (
            <WidgetSkeleton height={430} />
          ) : (
            <div className="skd-widget skd-widget--risk-panel">
              <div className="skd-widget-header skd-risk-panel-header">
                <div className="skd-widget-title skd-widget-title--danger">
                  <WarningAmberIcon
                    sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                  />
                  <span>{t("skdFleetAtRisk")}</span>
                  <WidgetInfo title={t("skdFleetAtRiskListInfo")} />
                </div>
                <div className="skd-search-wrap skd-risk-search">
                  <SearchIcon className="skd-search-icon" fontSize="small" />
                  <input
                    className="skd-search-input"
                    placeholder={t("skdSearchClient")}
                    value={riskSearch}
                    onChange={(e) => setRiskSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="skd-risk-panel-scroll skd-scroll-area">
                {atRiskClients.map((client, idx) => {
                    const barPct =
                      (client.vehicleCount / maxRiskVehicles) * 100;
                    return (
                      <div
                        className="skd-risk-list-item"
                        key={`${client.contractNumber}-${idx}`}
                      >
                        <div className="skd-risk-client">
                          <Tip
                            title={client.accountName}
                            className="skd-risk-account-name"
                            component="div"
                          >
                            {client.accountName}
                          </Tip>
                          {client.accountManagerName ? (
                            <Tip
                              title={client.accountManagerName}
                              className="skd-risk-am"
                              component="div"
                            >
                              {t("skdAm")}: {client.accountManagerName}
                            </Tip>
                          ) : null}
                        </div>
                        <div className="skd-risk-bar-wrap">
                          <div className="skd-risk-bar">
                            <span style={{ width: `${barPct}%` }} />
                          </div>
                        </div>
                        <Tip
                          title={`${fullNumber(client.vehicleCount)} ${t("skdVehShort")}`}
                          className="skd-risk-count skd-col-overdue"
                        >
                          {client.vehicleCount.toLocaleString()}{" "}
                          {t("skdVehShort")}
                        </Tip>
                      </div>
                    );
                  })}

                {!atRiskClients.length && (
                  <div className="skd-card-sub" style={{ padding: "12px 0" }}>
                    {t("skdNoData")}
                  </div>
                )}
              </div>

              {/*  <button type="button" className="skd-nudge-btn" disabled>
                <NotificationsActiveIcon sx={{ fontSize: 16 }} />
                {t("skdLiveSync")}
              </button> */}
            </div>
          )}
        </Grid>
      </Grid>

      {/* Row 3: Regional penetration, scorecard, AR breakdown */}
      <Grid container spacing={2} className="skd-section-row">
        <Grid item xs={12} md={6} lg={4}>
          {loading.fleetPenetration ? (
            <WidgetSkeleton height={400} />
          ) : (
            <div className="skd-widget skd-widget--penetration-panel">
              <div className="skd-widget-header skd-penetration-panel-header">
                <div className="skd-widget-title">
                  <span>{t("skdRegionalFleetPenetration")}</span>
                  <WidgetInfo title={t("skdRegionalFleetPenetrationInfo")} />
                </div>
                <div className="skd-penetration-toggle" role="tablist">
                  {[
                    {
                      type: "vehicle",
                      label: t("skdVehicle"),
                      Icon: DirectionsCarIcon,
                    },
                    {
                      type: "garage",
                      label: t("skdGarage"),
                      Icon: StorefrontIcon,
                    },
                  ].map(({ type, label, Icon }) => {
                    const isActive = penetrationView === type;
                    return (
                      <Tooltip
                        key={type}
                        title={<span style={{ fontSize: 13 }}>{label}</span>}
                        arrow
                        enterDelay={150}
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          aria-label={label}
                          className={`skd-penetration-tab skd-penetration-tab--${type}${
                            isActive ? " is-active" : ""
                          }`}
                          onClick={() => setPenetrationView(type)}
                        >
                          <span className="skd-penetration-tab-icon">
                            <Icon />
                          </span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
              <div className="skd-penetration-scroll skd-scroll-area">
                {penetrationRegions.length ? (
                  penetrationRegions.map((region, idx) => {
                    const tone = getPenetrationTone(region.percentage);
                    return (
                      <div
                        className="skd-penetration-row"
                        key={`${region.name}-${idx}`}
                      >
                        <div className="skd-penetration-header">
                          <Tip
                            title={region.name}
                            className="skd-penetration-name skd-col-fleet"
                          >
                            {region.name}
                          </Tip>
                          <Tip
                            title={`${fullNumber(region.count)} (${fullPct(region.percentage)})`}
                            className={`skd-penetration-count skd-penetration-pct--${tone}`}
                          >
                            {region.count.toLocaleString()}
                          </Tip>
                        </div>
                        <div className="skd-penetration-track">
                          <span
                            className={`skd-penetration-fill skd-penetration-fill--${tone}`}
                            style={{
                              width: `${Math.min(region.percentage, 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="skd-card-sub">{t("skdNoData")}</div>
                )}
              </div>
            </div>
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          {loading.scorecard ? (
            <WidgetSkeleton height={400} />
          ) : (
            <div className="skd-widget skd-widget--scorecard-panel">
              <div className="skd-widget-header skd-scorecard-panel-header">
                <div className="skd-widget-title">
                  <span>{t("skdAmPerformanceScorecard")}</span>
                  <WidgetInfo title={t("skdAmPerformanceScorecardInfo")} />
                </div>
                <div className="skd-search-wrap skd-scorecard-search">
                  <SearchIcon className="skd-search-icon" fontSize="small" />
                  <input
                    className="skd-search-input"
                    placeholder={t("skdSearchAm")}
                    value={scorecardSearch}
                    onChange={(e) => setScorecardSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="skd-scorecard-body">
                <table className="skd-table skd-scorecard-table">
                  <thead>
                    <tr>
                      <th>{t("skdAmName")}</th>
                      <th className="skd-col-accounts">
                        {t("skdScorecardNewAccounts")}
                      </th>
                      <th className="skd-col-vehicles">
                        {t("skdScorecardNewVeh")}
                      </th>
                      <th className="skd-col-outstanding">
                        {t("skdOutstanding")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorecardRows.map((row, idx) => {
                      const outstandingTone = getOutstandingTone(
                        row.outstanding,
                        maxScorecardOutstanding,
                      );
                      return (
                        <tr key={`${row.name}-${idx}`}>
                          <td className="skd-scorecard-am">
                            <Tip title={row.name}>
                              {row.rank}. {row.name}
                            </Tip>
                          </td>
                          <td className="skd-col-accounts">
                            <Tip
                              title={`+${fullNumber(row.newAccounts)} ${t("skdAccShort")}`}
                            >
                              +{row.newAccounts.toLocaleString()}{" "}
                              {t("skdAccShort")}
                            </Tip>
                          </td>
                          <td className="skd-col-vehicles">
                            <Tip
                              title={`+${fullNumber(row.newVehicles)} ${t("skdVehShort")}`}
                            >
                              +{row.newVehicles.toLocaleString()}{" "}
                              {t("skdVehShort")}
                            </Tip>
                          </td>
                          <td
                            className={`skd-col-outstanding skd-scorecard-outstanding skd-scorecard-outstanding--${outstandingTone}`}
                          >
                            <Tip
                              title={fullCurrency(row.outstanding, currency)}
                            >
                              {formatCurrency(row.outstanding, currency)}
                            </Tip>
                          </td>
                        </tr>
                      );
                    })}
                    {!scorecardRows.length && (
                      <tr>
                        <td
                          colSpan={4}
                          style={{ color: COLORS.muted, textAlign: "center" }}
                        >
                          {t("skdNoData")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Grid>
        <Grid item xs={12} lg={3} sx={{ minWidth: 0, maxWidth: "100%" }}>
          {loading.fleetOverdue ? (
            <WidgetSkeleton height={400} />
          ) : (
            <div className="skd-widget skd-widget--fixed-h-md skd-ar-layout">
              <div className="skd-widget-title skd-ar-title">
                <span>{t("skdCashCollectionAr")}</span>
                <WidgetInfo title={t("skdCashCollectionArInfo")} />
              </div>

              <div className="skd-ar-chart-wrap" ref={arChartWrapRef}>
                <HighchartsReact
                  ref={arChartRef}
                  highcharts={Highcharts}
                  options={arBreakdownChartOptions}
                  containerProps={{
                    style: { width: "100%", height: "100%", maxWidth: "100%" },
                  }}
                />
              </div>

              {/* Legend hidden — colors shown on stat cards below
              <div className="skd-ar-legend">
                ...
              </div>
              */}

              <div className="skd-ar-stats-side">
                <div className="skd-ar-stat-card skd-ar-stat-card--outstanding">
                  <span className="skd-ar-stat-icon" />
                  <div className="skd-ar-stat-content">
                    <span className="skd-ar-stat-label">
                      {t("skdTotalAccountOutstanding")}
                    </span>
                    <Tip
                      title={fullCurrency(overdue.totalOutstanding, currency)}
                      className="skd-ar-stat-value"
                    >
                      {formatCurrency(overdue.totalOutstanding, currency)}
                    </Tip>
                  </div>
                </div>
                <div className="skd-ar-stat-card skd-ar-stat-card--overdue">
                  <span className="skd-ar-stat-icon" />
                  <div className="skd-ar-stat-content">
                    <span className="skd-ar-stat-label">
                      {t("skdTotalOverdue30Days")}
                    </span>
                    <Tip
                      title={fullCurrency(overdue.totalOverdue, currency)}
                      className="skd-ar-stat-value"
                    >
                      {formatCurrency(overdue.totalOverdue, currency)}
                    </Tip>
                  </div>
                </div>
                <div className="skd-ar-stat-card skd-ar-stat-card--collected">
                  <span className="skd-ar-stat-icon" />
                  <div className="skd-ar-stat-content">
                    <span className="skd-ar-stat-label">
                      {t("skdTotalCollectedMtd")}
                    </span>
                    <Tip
                      title={fullCurrency(overdue.totalCollected, currency)}
                      className="skd-ar-stat-value"
                    >
                      {formatCurrency(overdue.totalCollected, currency)}
                    </Tip>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Grid>
      </Grid>

      {/* Row 4: Client directory + Fleet make & model + Product mix */}
      <Grid container spacing={2} className="skd-section-row">
        <Grid item xs={12} md={6} lg={4}>
          <ClientDirectoryWidget
            loading={loading.fleetOverdue}
            data={fleetOverdue}
            currency={currency}
            t={t}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <FleetMakeModelWidget
            loading={loading.fleetMakeModel}
            data={fleetMakeModel}
            t={t}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ProductMixPmsWidget
            loading={loading.productMix}
            data={productMix}
            t={t}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state) => ({
  logInUser: state.logInUsers,
  ServerSetting: state.ServerSetting,
  cities: state.Cities?.cities || [],
  userCities: state.Cities?.userCities || [],
});

export default connect(mapStateToProps)(SalesKPIDashboard);
